import gsap from 'gsap';
import { PageEvent } from './page-lifecycle/PageEvent';
import header from './header';
import { HHDSButtonHeader, HHDSButtonHeaderAttrNames } from '../components/ButtonHeader/ButtonHeader';
import { toggleScrollbarForcer } from '../utils/scrollbar-forcer';
import { barbaController } from './page-lifecycle/barba/BarbaController';
import { CSSBreakpoint } from './constants-theme';
import { lockPageScroll } from '../components/ComponentUtils';

export const enum MenuState {
	Open = 'open',
	Animating = 'animating',
	Closed = 'closed',
}

export enum MenuEvent {
	opening = 'opening',
	closed = 'closed',
	reverseComplete = 'reverseComplete',
}

export enum MenuCloseReason {
	PageTransition = 'pageTransition',
	UserInteraction = 'userInteraction',
}

const DEBUG_VERBOSE = false;

let START_TIME = 0;
export class HHDSMenu extends EventTarget {
	private _animation: gsap.core.Timeline = gsap.timeline({ paused: true });

	private _menu: HTMLElement | null = null;
	private _menuBtn: HHDSButtonHeader | null = null;
	private _weglotBtn: HTMLInputElement | null = null;
	private _links: NodeListOf<Element> | null = null;
	private menuLinkEls!: HTMLAnchorElement[];

	public state: MenuState = MenuState.Closed;

	constructor() {
		super();
		this.init();
	}

	private emitEvent(event: MenuEvent, data: any = null) {
		const customEvent = new CustomEvent(event, { detail: data });
		this.dispatchEvent(customEvent);
	}

	init() {
		this._menu = document.querySelector('.menu') as HTMLElement;
		this._menuBtn = document.querySelector('#header-burger') as HHDSButtonHeader;
		this._weglotBtn = document.querySelector('#menu__weglot .weglot_choice') as HTMLInputElement;
		this._links = document.querySelectorAll('.menu-items-list > li');

		if (this._menu) {
			this.addListeners();
			this.addResizeObserver();
		}
	}

	// ////////////////////////////////////////////////////////////////////

	private addResizeObserver() {
		// If burger menu is open and site resized to desktop then close the menu
		const resizeObserver = new ResizeObserver(() => {
			if (window.matchMedia(`(min-width: ${CSSBreakpoint.Desktop}px)`).matches) {
				if (this.state === MenuState.Open) {
					this.close(MenuCloseReason.UserInteraction);
				}
			}
		});

		resizeObserver.observe(document.documentElement);
	}

	// ////////////////////////////////////////////////////////////////////

	addListeners(): void {
		document.addEventListener(PageEvent.enter, (event: any) => {
			const parser = new DOMParser();
			let dom = parser.parseFromString(event.detail?.next?.html, 'text/html');
			this.refresh(dom);
		});

		this._links?.forEach((link) => {
			link.addEventListener('mouseenter', (event) => {
				const target = event.target as HTMLElement;

				target?.classList.toggle('active', true);

				const matchingClass = Array.from(target.classList).find((cls) => /^menu-item-\d+$/.test(cls));

				if (matchingClass) this.showThumb(matchingClass);
			});

			link.addEventListener('mouseleave', (event) => {
				const target = event.target as HTMLElement;
				target?.classList.toggle('active', false);
				this.showCurrentThumb();
			});

			if (DEBUG_VERBOSE) {
				link.addEventListener('click', () => {
					START_TIME = Date.now();
				});
			}
		});

		if (!this._menu) return;

		this.menuLinkEls = Array.from(this._menu.querySelectorAll('.menu-items-list a'));
	}

	private setMenuState(state: MenuState) {
		this.state = state;
		DEBUG_VERBOSE && console.log('setMenuState:', state);
	}

	public open() {
		DEBUG_VERBOSE && logger.log('open');

		if (this.state === MenuState.Open) return;

		barbaController.useTransitions = false;

		this.a11yOnOpen();

		this._menuBtn?.classList.toggle('header__button--active', true);
		this._menuBtn?.setAttribute(HHDSButtonHeaderAttrNames.active, 'true');

		gsap.set('.menu-images-list li img', { opacity: 0 });
		gsap.set('#menu__weglot', { opacity: 0 });

		this.setMenuState(MenuState.Animating);
		this.emitEvent(MenuEvent.opening);

		this.toggleHeaderOpenClass(true);

		const SPEED: number = 2000;
		const DURATION: number = window.innerHeight / SPEED;

		/* Clear out the previous timeline */
		this._animation.clear();
		this._animation.timeScale(1);

		toggleScrollbarForcer(true);
		lockPageScroll(true);

		this._animation.eventCallback('onComplete', () => {
			this.setMenuState(MenuState.Open);

			/**
			 * a11y - Force focus onto first element as iphone was losing focus on
			 * the menu button and moving onto obscured page content
			 */

			const firstLinkEl = this.menuLinkEls[0];

			if (firstLinkEl) firstLinkEl.focus();
		});

		const selectorMenuItemListItems = '.menu-items-list > li';

		// Set the text to be invisible and slightly offset
		this._animation.set(selectorMenuItemListItems, { opacity: 0, y: '+=20' });

		// // Set the image panel to be invisible and scaled down
		this._animation.set('.menu-images', { opacity: 0 });

		// Slide in the menu background
		this._animation.to(
			this._menu,
			{
				y: '0%',
				duration: DURATION,
				ease: 'Power3.easeInOut',
			},
			0
		);

		// Slide in the images panel
		this._animation.to('.menu-images', {
			opacity: 1,
			scaleY: 1,
			duration: 0.3,
		});

		// Animate in the text links
		const links = document.querySelectorAll(selectorMenuItemListItems);

		this._animation.to(
			links,
			{
				y: 0,
				opacity: 1,
				stagger: {
					amount: 0.1,
					each: 0.3,
				},
				onUpdate: function (this: gsap.core.Tween) {
					const animation = this as gsap.core.Tween & { _calledShowCurrentThumb?: boolean };

					if (animation.progress() >= 0.5 && !animation._calledShowCurrentThumb) {
						animation._calledShowCurrentThumb = true;
						if (typeof animation.vars.onHalfway === 'function') {
							animation.vars.onHalfway();
						}
					}
				},
				onHalfway: () => {
					this.showCurrentThumb();
				},
				onComplete: function (this: gsap.core.Tween) {
					(this as gsap.core.Tween & { _calledShowCurrentThumb?: boolean })._calledShowCurrentThumb = false;
				},
			},
			'-=0.4'
		);

		gsap.matchMedia().add('(max-width: 833px)', () => {
			this._animation.to('#menu__weglot', {
				opacity: 1,
				duration: 0.3,
			});
		});

		this._animation.play();
	}

	private a11yOnOpen() {
		if (this._menu) {
			this._menu.setAttribute('aria-expanded', 'true');
			this._menu.hidden = false;
			this._menu.style.display = 'flex';
		}
	}

	private a11yOnClose() {
		if (this._menu) {
			this._menu.setAttribute('aria-expanded', 'false');
			this._menu.hidden = true;
			this._menu.style.display = 'none';
		}
	}

	public close(reason: MenuCloseReason) {
		DEBUG_VERBOSE && logger.log('close');

		if (this.state === MenuState.Closed) return;

		barbaController.useTransitions = true;

		this._menuBtn?.classList.toggle('header__button--active', false);
		this._menuBtn?.setAttribute(HHDSButtonHeaderAttrNames.active, 'false');
		this.closeWeglot();

		this.setMenuState(MenuState.Animating);

		this._animation.timeScale(1.5);

		this._animation.eventCallback('onReverseComplete', () => {
			DEBUG_VERBOSE && logger.log('onReverseComplete');
			this.setMenuState(MenuState.Closed);
			toggleScrollbarForcer(false);
			lockPageScroll(false);

			this.toggleHeaderOpenClass(false);

			this.emitEvent(MenuEvent.reverseComplete, { viaPageTransition: reason == MenuCloseReason.PageTransition });
			this.a11yOnClose();
			this.emitEvent(MenuEvent.closed, { viaPageTransition: reason == MenuCloseReason.PageTransition });

			if (DEBUG_VERBOSE) {
				const endTime = Date.now() - START_TIME;
				console.log(`Time to navigate: ${endTime / 1000}s`);
			}
		});

		this._animation?.reverse();
	}

	private toggleHeaderOpenClass(open: boolean) {
		header?.element?.classList.toggle('header--open', open);

		// toggling of header--open on only the Scrolled header variant was introduced to resolve DD#124
		// but introduces DD#166, which is incorrect dark text on one of the headers. Removed, as it appears
		// the no-scroll fixes in commit fbd1580 resolves DD#124 on its own.
		//header?.scrolledHeader?.classList.toggle('header--open', open);
	}

	public refresh(newPage: any) {
		const currentPage = newPage.querySelector('.menu-items-list li.current_page_item') as HTMLElement;

		if (!currentPage) return;

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		// Extract the id from the menu-item-* class

		const menuItemID = Array.from(currentPage.classList).find((cls) => /^menu-item-\d+$/.test(cls));

		if (menuItemID) {
			let linksAndImages = Array.from(document.querySelectorAll('.menu-items-list li, .menu-images-list li')) as HTMLElement[];

			linksAndImages?.forEach((link: HTMLElement) => {
				DEBUG_VERBOSE && logger.log(this.refresh.name, link, link.classList.contains(menuItemID));

				link.classList.toggle('current_page_item', link.classList.contains(menuItemID));
			});
		}
	}

	private showThumb(id: string) {
		DEBUG_VERBOSE && logger.log(this.showThumb.name, id);

		gsap.to('.menu-images-list li img', {
			opacity: (_, target) => {
				const elId = target.parentElement.getAttribute('data-id');
				return elId === id ? 1 : 0;
			},
			duration: 0.7,
		});
	}

	private showCurrentThumb() {
		DEBUG_VERBOSE && logger.log(this.showCurrentThumb.name);

		let activeMenuEl = document.querySelector(`.menu-items-list li.current_page_item`) as HTMLElement;

		if (activeMenuEl) {
			const parentMenuItem = activeMenuEl.parentElement?.closest('.menu-item') as HTMLElement;

			/**
			 * If activeMenuEl has a parent menu-item then we use the id from that
			 * instead as we want all sub-menu items to use the parent image.
			 */

			if (parentMenuItem) activeMenuEl = parentMenuItem;

			const matchingClass = Array.from(activeMenuEl.classList).find((cls) => /^menu-item-\d+$/.test(cls));

			if (matchingClass) this.showThumb(matchingClass);
		} else {
			// Show a fallback image

			let activeMenuEl = document.querySelector(`.menu-items-list li`) as HTMLElement;

			const matchingClass = Array.from(activeMenuEl.classList).find((cls) => /^menu-item-\d+$/.test(cls));

			if (matchingClass) this.showThumb(matchingClass);
		}
	}

	private closeWeglot(): void {
		if (this._weglotBtn) {
			this._weglotBtn.checked = false;
		}
	}
}

export default new HHDSMenu();
